---
id: resume-parser-service-go
title: Resume parser service
owner: Backend Team
status: Active
lastReviewed: 2026-02-02
visibility: internal
tags:
  - resume
  - parsing
  - microservice
---

## Product Overview

### Who it serves

This microservice powers resume ingestion workflows that convert uploaded files or raw text into structured resume data for downstream resume creation and editing experiences.

### Primary workflow

Authenticated clients upload a resume file to parse, or send raw resume text for creation. The service returns structured resume data and, for text-based creation, persists a new resume record.

### Business rules

Versioned routes enforce authentication for most parsing endpoints, while an anonymous v3 parse endpoint is available for unauthenticated use. Deprecated v1 endpoints return gone responses, and parsing behavior varies by API version and file type.

### Outcomes

Clients receive normalized resume data for use in resume builder and review flows, and optional storage of uploaded files and created resumes supports later retrieval and editing.

### Out of scope

Subscription checks, user-facing UI, and downstream template rendering live outside this service.

### API documentation

[Internal API Documentation](https://wiki.rezi.ai/apis/resume-parser-service-go-docs)

## Technical Overview

### Architecture flow

Routes in `apps/micro-services/resume-parser-service-go/pkg/api/routes/routes.go` compose the parser controller with converters, GPT/Gemini callers, storage, and Firestore-backed repositories, then expose versioned Gin routes.

### Entry points

The service binary starts from `apps/micro-services/resume-parser-service-go/cmd/resume-parser/main.go`, which loads config, initializes Firebase/GCS, and runs the API server. The OpenAPI generator entry point is `apps/micro-services/resume-parser-service-go/cmd/spec/main.go`, which builds `spec.json` from route definitions.

### Key modules

Parsing orchestration lives in `apps/micro-services/resume-parser-service-go/pkg/services/parsers/resume.go`, GPT calls in `apps/micro-services/resume-parser-service-go/pkg/services/callers/gpt.go`, PDF-to-image conversion in `apps/micro-services/resume-parser-service-go/pkg/services/converters/pdf.go`, and controller request handling in `apps/micro-services/resume-parser-service-go/pkg/api/controllers/parser.go`.

### Data sources and sinks

Uploaded files can be stored in GCS via `apps/micro-services/resume-parser-service-go/pkg/repo/storage/gcs`, and resume creation persists structured data into Firestore via `apps/micro-services/resume-parser-service-go/pkg/repo/resume`.

### Integrations and side effects

The service integrates with Firebase authentication, GCS storage, the image-service for PDF conversion, OpenAI GPT for v2 parsing, and Gemini for v3 parsing and text-based resume creation. OCR support is optional and enabled only when built with the `tesseract` tag.

### Constraints and risks

Parsing depends on external AI services, cloud storage availability, and valid credentials at startup. File parsing is sensitive to file type detection and may return unsupported type errors if signatures do not match PDF/DOCX/DOC headers.

## Changelog

- 2026-02-03: Can have custom token
- 2026-02-02: Initial documentation.
